// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import { teamLogoEndpoints } from '@/modules/team-logo/team-logo.constants'
import type {
  TTeamLogo,
  TCreateTeamLogoRequest,
  TReplaceTeamLogoRequest,
  TSetTeamLogoActiveRequest,
} from '@/modules/team-logo/team-logo.types'

export async function getTeamLogoList(
  params: TAdminListParams
): Promise<TResponse<TTeamLogo[]>> {
  const response = await api.get<TResponse<TTeamLogo[]>>(
    teamLogoEndpoints.list(params)
  )

  return response.data
}

// D6 presigned upload: ask the API for a short-lived PUT URL, then upload the
// file straight to S3 (NOT through `api` — no auth header to S3). Returns the
// object key the API uses to finalize/replace.
async function uploadObject(file: File): Promise<string> {
  const contentType = file.type || 'application/octet-stream'
  const presign = await api.post<
    TResponse<{ upload_url: string; key: string }>
  >(teamLogoEndpoints.presign, { content_type: contentType })
  const { upload_url, key } = presign.data.data

  const put = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!put.ok) {
    throw new Error(`S3 upload failed (${put.status})`)
  }
  return key
}

export async function createTeamLogo(
  payload: TCreateTeamLogoRequest
): Promise<void> {
  const key = await uploadObject(payload.file)
  await api.post<TResponse<TTeamLogo>>(teamLogoEndpoints.finalize, { key })
}

// Replace an existing system logo's image — version bumps server-side so clients
// refetch (same id, new version).
export async function replaceTeamLogo(
  payload: TReplaceTeamLogoRequest
): Promise<void> {
  const key = await uploadObject(payload.file)
  await api.patch(teamLogoEndpoints.replace(payload.id), { key })
}

export async function setTeamLogoActive({
  id,
  is_active,
}: TSetTeamLogoActiveRequest): Promise<void> {
  await api.patch(teamLogoEndpoints.setActive(id), { is_active })
}

export async function deleteTeamLogo(id: string): Promise<void> {
  await api.delete(teamLogoEndpoints.delete(id))
}
